/* eslint-disable no-async-promise-executor */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable @typescript-eslint/indent */
import Currency from '@components/common/Currrency/Currency';
import { areChildrenAllowed, isProductUnAvailable } from '@helpers/product';
import React, { useContext, useState, useRef, useEffect } from 'react';
import { store } from '@context/store';
import toggleErrorPopup from '@helpers/toggleErrorPopup';
import { useCookies } from 'react-cookie';
import { addToCart as addToCartEvent, addonSelected } from '@events/DLP';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import { ProductDetailsProps } from '@customTypes/product-details';
import useAddOns from '@hooks/useAddons';
import useAddToCart from '@hooks/useAddToCart';
import User from '@customTypes/user';
import CounterVariant from '@components/ProductDetails/ProductDetailsVariant/Components/CounterVariant';
import useCounterValidation from '@hooks/useCounterValidation';
import Addons from '@components/common/Addons';
import useAlgoliaEvents from '@hooks/useAlgoliaEvents';
import useHotSpot from '@hooks/useHotSpot';
import { FAIR_PRICING_VARIANT } from '@constants/fairPricing';
import { getProductPriceForGuestType } from '@utils/fairPricing';
import ImageLoaded from '@components/common/ImageLoaded/ImageLoaded';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';
import SpaBanner from '@components/ProductDetails/SpaBanner';
import DiscountPriceWithSTP from '@components/STP/ProductDetails/DiscountPriceWithSTP';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import ProductDescription from './ProductDescription';
import ProductDetailsFooter from './ProductDetailsFooter';
import FormErrorText from './FormErrorText';

function IndividualBilledProductDetails({
  cartData,
  date,
  hotelDetails,
  product,
  setCartData,
  setIsDrawerOpen,
  showFairPricing,
}: ProductDetailsProps) {
  const {
    state: { pricingMarkdownExperiment },
    dispatch,
  } = useContext(store);
  const [adultCounter, setAdultCounter] = useState<number>(1);
  const [childrenCounter, setChildrenCounter] = useState<number>(0);

  const [addToCart] = useAddToCart();
  const { ctasUpdateExperiment } = useFeatureFlagContext();
  const { addOnItems, resetAddOns, updateAddonItem, onAddonUnitsSelect, showAddons } = useAddOns(
    product,
    hotelDetails,
    addonSelected,
    adultCounter,
  );
  const { counterValidation, errorMessage, showErrorMessage } = useCounterValidation(
    adultCounter,
    childrenCounter,
    cartData,
    product,
  );
  const { applyHotSpotDiscountToTotal, isDateWithinVisitWindow } = useHotSpot();

  const [cookies] = useCookies();
  const { getItem } = useSessionStorage();
  const { algoliaAddedToCartEvent } = useAlgoliaEvents();

  const user: User = cookies.userInformation;
  const sessionID = getItem(sessionStorageKeys.SESSION_ID);
  const errorRef = useRef<HTMLDivElement>(null);

  const handleFooterButtonClick = async () =>
    new Promise(async (resolve, reject) => {
      if (date && adultCounter > 0) {
        try {
          const { updatedCartData } = await addToCart(
            addOnItems,
            adultCounter,
            childrenCounter,
            date,
            cartData,
            product,
            hotelDetails,
            dispatch,
            user,
          );

          if (updatedCartData) {
            setCartData(updatedCartData);
          }

          // Fire events
          addToCartEvent(
            product,
            hotelDetails,
            addOnItems,
            adultCounter,
            childrenCounter,
            updatedCartData,
            date!,
          );

          algoliaAddedToCartEvent(hotelDetails, user, sessionID);

          // Reset to defaults.
          setAdultCounter(0);
          setChildrenCounter(0);
          setIsDrawerOpen(false);
          resetAddOns();
          resolve(updatedCartData);
        } catch (error) {
          toggleErrorPopup(error, dispatch);
          reject(error);
        }
      }
    });

  const handleAdultCounterChange = (adultCount: number) => {
    const maxChildrenPerAdult = adultCount * product.max_children_per_adult;

    if (maxChildrenPerAdult < childrenCounter) {
      setChildrenCounter(maxChildrenPerAdult);
    }
  };

  useEffect(() => {
    if (showErrorMessage && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showErrorMessage]);

  return (
    <div className="w-full">
      <div className="h-250px w-full relative">
        <ImageLoaded url={product.photo_url.url} alt="product" layout="fill" />
      </div>

      <div className="w-full px-4 pt-5 pb-5">
        {/* Adult */}
        <div className="flex flex-row items-center justify-between border-b border-rp-gray-divider pb-6">
          <div className="flex flex-col">
            <div className="flex items-baseline">
              <p className="font-rp-pn-semi-bold leading-6 text-18">
                {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                  ? 'Adults'
                  : 'Adult'}
              </p>

              {product.adult_age_restriction_text && (
                <p className="first-letter-uppercase font-rp-pn-regular leading-18px ml-2 opacity-50 text-sm">
                  {product.adult_age_restriction_text}
                </p>
              )}
            </div>
            <div className="flex font-rp-pn-regular items-center text-custom-black">
              {!date && <p className="leading-22px mr-1 text-sm">Starting at</p>}

              {date &&
              product.is_hot_spot &&
              hotelDetails.hot_spot &&
              isDateWithinVisitWindow(date, hotelDetails) ? (
                <div className={`${date && product.is_hot_spot ? 'mr-2' : ''}`}>
                  <Currency
                    value={applyHotSpotDiscountToTotal(
                      +product.adult_price,
                      hotelDetails.hot_spot,
                      showFairPricing,
                    )}
                    valueClasses="leading-4 text-rp-hot-spot text-md"
                  />
                </div>
              ) : null}

              {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
                <DiscountPriceWithSTP
                  cartData={cartData}
                  date={date}
                  hotelDetails={hotelDetails}
                  personType="adult"
                  product={product}
                  showFairPricing={showFairPricing}
                  individualBilling
                />
              ) : (
                <Currency
                  value={getProductPriceForGuestType(product, 'adult', showFairPricing)}
                  valueClasses={`leading-4 ${
                    date && product.is_hot_spot && isDateWithinVisitWindow(date, hotelDetails)
                      ? 'text-rp-disabled text-base line-through'
                      : 'text-custom-black text-md'
                  }`}
                />
              )}
            </div>

            {!(date && isProductUnAvailable(product, cartData, hotelDetails, date)) &&
            showFairPricing ? (
              <p className="text-sm text-rp-gray whitespace-nowrap d:-mt-1 d:mb-1">
                {FAIR_PRICING_VARIANT === 'variant1'
                  ? 'Includes fees'
                  : `+$${(+getProductPriceForGuestType(product, 'adult', showFairPricing)).toFixed(
                      0,
                    )} in fees`}
              </p>
            ) : null}
          </div>

          <div className="flex items-center space-x-4">
            <CounterVariant
              dispatch={dispatch}
              setParentCounter={setAdultCounter}
              counter={adultCounter}
              productData={product}
              date={date}
              isDisabled={date && isProductUnAvailable(product, cartData, hotelDetails, date)}
              personType="adults"
              onChange={handleAdultCounterChange}
              validate={counterValidation}
              ctasUpdateExperiment={ctasUpdateExperiment}
            />
          </div>
        </div>

        <SpaBanner product={product} />

        {/* Children */}
        {areChildrenAllowed(product) && (
          <div className="flex flex-col border-b border-rp-gray-divider pb-6 mt-6">
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-col">
                <div className="flex items-baseline">
                  <p className="font-rp-pn-semi-bold leading-6 text-18">
                    {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                      ? 'Children'
                      : 'Child'}
                  </p>

                  {product.adult_age_restriction_text && (
                    <p className="first-letter-uppercase font-rp-pn-regular leading-18px ml-2 opacity-50 text-sm">
                      {product.child_age_restriction_text}
                    </p>
                  )}
                </div>

                <div className="flex font-rp-pn-regular items-center text-custom-black">
                  {!date && <p className="leading-22px mr-1 text-sm">Starting at</p>}

                  {date &&
                  product.is_hot_spot &&
                  hotelDetails.hot_spot &&
                  isDateWithinVisitWindow(date, hotelDetails) ? (
                    <div className={`${date && product.is_hot_spot ? 'mr-2' : ''}`}>
                      <Currency
                        value={applyHotSpotDiscountToTotal(
                          +product.children_price,
                          hotelDetails.hot_spot,
                          showFairPricing,
                        )}
                        valueClasses="leading-4 text-rp-hot-spot text-md"
                      />
                    </div>
                  ) : null}
                  {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
                    <DiscountPriceWithSTP
                      cartData={cartData}
                      date={date}
                      hotelDetails={hotelDetails}
                      personType="child"
                      product={product}
                      showFairPricing={showFairPricing}
                      individualBilling
                    />
                  ) : (
                    <Currency
                      value={getProductPriceForGuestType(product, 'child', showFairPricing)}
                      valueClasses={`leading-4 ${
                        date && product.is_hot_spot && isDateWithinVisitWindow(date, hotelDetails)
                          ? 'text-rp-disabled text-base line-through'
                          : 'text-custom-black text-md'
                      }`}
                    />
                  )}
                </div>

                {!(date && isProductUnAvailable(product, cartData, hotelDetails, date)) &&
                showFairPricing ? (
                  <p className="text-sm text-rp-gray whitespace-nowrap d:-mt-1 d:mb-1">
                    {FAIR_PRICING_VARIANT === 'variant1'
                      ? 'Includes fees'
                      : `+$${(+getProductPriceForGuestType(
                          product,
                          'child',
                          showFairPricing,
                        )).toFixed(0)} in fees`}
                  </p>
                ) : null}
              </div>

              <div className="flex items-center space-x-4">
                <CounterVariant
                  dispatch={dispatch}
                  setParentCounter={setChildrenCounter}
                  counter={childrenCounter}
                  productData={product}
                  date={date}
                  isDisabled={date && isProductUnAvailable(product, cartData, hotelDetails, date)}
                  personType="children"
                  validate={counterValidation}
                  ctasUpdateExperiment={ctasUpdateExperiment}
                />
              </div>
            </div>
          </div>
        )}

        {/* Infants */}
        {product.infants_allowed && (
          <div className="flex flex-row items-center justify-between border-b border-rp-gray-divider pb-6 mt-6">
            <div className="flex items-baseline">
              <p className="font-rp-pn-semi-bold leading-6 text-18">Infant</p>

              {product.min_child_age && (
                <p className="capitalize font-rp-pn-regular leading-18px ml-2 opacity-50 text-sm">
                  Over {product.min_child_age}
                </p>
              )}
            </div>

            <p className="leading-6 mr-1 text-md">Free</p>
          </div>
        )}

        {/* Error message */}
        {showErrorMessage && (
          <div className="mt-6" ref={errorRef}>
            <FormErrorText showInfoIcon text={errorMessage} />
          </div>
        )}

        {/* Addons */}
        <Addons
          addons={addOnItems}
          onAddonUnitsSelect={onAddonUnitsSelect}
          showAddons={showAddons}
          updateAddonItem={updateAddonItem}
        />

        {/* Product description */}
        {product.description && <ProductDescription product={product} />}
      </div>

      <div className="mt-16">
        <ProductDetailsFooter
          date={date}
          cartData={cartData}
          hotelDetails={hotelDetails}
          onButtonClick={handleFooterButtonClick}
          product={product}
          adultCounter={adultCounter}
        />
      </div>
    </div>
  );
}

export default IndividualBilledProductDetails;
